import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Parser, Player } from 'svga-web';
import { apiGet, apiPost, apiDelete, apiUpload, dataUrlToBlob } from '../api';
import { formatNumberPrecise } from '../Utils';
import VapPreview from './VapPreview';

const GiftUpload = () => {
  const [giftName, setGiftName] = useState('');
  const [giftPrice, setGiftPrice] = useState('');
  const [svgaFile, setSvgaFile] = useState(null);
  const [vapFile, setVapFile] = useState(null);
  const [animType, setAnimType] = useState('svga'); // 'svga' | 'vap'
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [svgaPreviewData, setSvgaPreviewData] = useState(null);
  const previewCanvasRef = useRef(null);
  const previewPlayerRef = useRef(null);
  const [uploadedGifts, setUploadedGifts] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const fetchGifts = useCallback(async () => {
    try {
      const gifts = await apiGet('/api/gifts/all');
      setUploadedGifts(gifts);
    } catch (e) {
      setErrorText(`Failed to load gifts: ${e.message}`);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { fetchGifts(); }, [fetchGifts]);

  useEffect(() => {
    if (svgaPreviewData && previewCanvasRef.current) {
      if (previewPlayerRef.current) previewPlayerRef.current.destroy();
      const player = new Player(previewCanvasRef.current);
      player.mount(svgaPreviewData).then(() => {
        previewPlayerRef.current = player;
        player.start();
      });
    }
    return () => { if (previewPlayerRef.current) { previewPlayerRef.current.destroy(); previewPlayerRef.current = null; } };
  }, [svgaPreviewData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSvgaFile(null); setVapFile(null); setThumbnailPreview(null); setSvgaPreviewData(null);
    setStatusText(''); setErrorText('');
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.mp4')) {
      // Tencent VAP video gift — transparent preview + thumbnail handled by <VapPreview/>
      setAnimType('vap');
      setVapFile(file);
      setStatusText('Reading VAP file… generating transparent preview');
      return;
    }
    if (lower.endsWith('.svga')) {
      setAnimType('svga');
      setSvgaFile(file); setStatusText('Reading file...');
      const reader = new FileReader();
      reader.onload = async () => {
        let thumbnailPlayer = null;
        try {
          setStatusText('Generating thumbnail and preview...');
          const buffer = reader.result;
          const parser = new Parser();
          const svgaData = await parser.do(buffer);
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = 200;
          tempCanvas.height = 200;
          thumbnailPlayer = new Player(tempCanvas);
          const totalFrames = svgaData.frames;
          if (typeof totalFrames !== 'number' || totalFrames <= 0) throw new Error("Could not determine total frames.");
          const targetFrame = Math.max(1, Math.floor(totalFrames / 2));
          await thumbnailPlayer.mount(svgaData);
          thumbnailPlayer.set({ loop: 1, fillMode: 'forwards', startFrame: targetFrame - 1, endFrame: targetFrame });
          const getThumbnailPromise = new Promise((resolve, reject) => {
            thumbnailPlayer.$on('end', () => resolve(tempCanvas.toDataURL('image/webp', 0.75)));
            thumbnailPlayer.$on('error', reject);
            thumbnailPlayer.start();
          });
          const generatedThumbnail = await getThumbnailPromise;
          setThumbnailPreview(generatedThumbnail);
          setSvgaPreviewData(svgaData);
          setStatusText('Thumbnail ready!');
        } catch (error) {
          setErrorText(`Error: Could not process the SVGA file. ${error.message}`);
        } finally {
          if (thumbnailPlayer) thumbnailPlayer.destroy();
        }
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    setErrorText('Invalid file type. Please select a .svga or .mp4 (VAP) file.');
  };

  const uploadGift = async (e) => {
    e.preventDefault();
    const animFile = animType === 'vap' ? vapFile : svgaFile;
    if (!giftName || !giftPrice || !animFile || !thumbnailPreview) {
      setErrorText('Please provide all information and wait for the thumbnail.'); return;
    }
    setLoading(true); setErrorText('');
    try {
      setStatusText(`Uploading ${animType.toUpperCase()} file...`);
      const { url: animUrl } = await apiUpload('gifts', animFile, `${Date.now()}_${animFile.name}`);
      setStatusText('Uploading thumbnail...');
      const thumbBlob = dataUrlToBlob(thumbnailPreview);
      const baseName = animFile.name.replace(/\.(svga|mp4)$/i, '');
      const { url: thumbnailUrl } = await apiUpload('gifts', thumbBlob, `${baseName}_thumb.webp`);
      setStatusText('Saving to database...');
      const payload = { name: giftName, price: parseFloat(giftPrice), thumbnailUrl, animType };
      if (animType === 'vap') payload.vapUrl = animUrl; else payload.svgaUrl = animUrl;
      await apiPost('/api/gifts', payload);
      alert('Gift uploaded successfully!');
      setGiftName(''); setGiftPrice(''); setSvgaFile(null); setVapFile(null); setAnimType('svga');
      setThumbnailPreview(null); setSvgaPreviewData(null); setStatusText(''); setErrorText('');
      if (document.getElementById('file-input')) document.getElementById('file-input').value = '';
      fetchGifts();
    } catch (error) {
      setErrorText(`Upload failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (gift) => {
    if (!window.confirm(`Delete gift "${gift.name}"? This cannot be undone.`)) return;
    try {
      await apiDelete(`/api/gifts/${gift.id}`);
      alert('Gift deleted successfully!');
      fetchGifts();
    } catch (error) {
      alert(`Failed to delete: ${error.message}`);
    }
  };

  return (
    <div className="dark-page">
      {/* Upload Form */}
      <div className="dark-upload-card">
        <h3>Upload New Gift 🎁</h3>
        <form onSubmit={uploadGift}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <label htmlFor="giftName">Gift Name</label>
              <input type="text" id="giftName" value={giftName} onChange={e => setGiftName(e.target.value)} required placeholder="e.g. Rose, Heart..." />
              <label htmlFor="giftPrice">Gift Price (Diamond)</label>
              <input type="number" id="giftPrice" min="0" value={giftPrice} onChange={e => setGiftPrice(e.target.value)} required placeholder="e.g. 100" />
              <label htmlFor="file-input">Gift File (SVGA or VAP .mp4)</label>
              <input type="file" id="file-input" accept=".svga,.mp4" onChange={handleFileChange} required />
            </div>
            <div className="preview-zone">
              {animType === 'vap' && vapFile ? (
                <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%', gap: 12, alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Transparent preview <span style={{ color: '#a78bfa' }}>(VAP)</span></p>
                    <VapPreview
                      file={vapFile}
                      width={100}
                      height={160}
                      onReady={(cfg) => setStatusText(`VAP ready — ${cfg.rgbFrame[2]}×${cfg.rgbFrame[3]}`)}
                      onThumbnail={(d) => setThumbnailPreview(d)}
                      onError={(m) => setErrorText(m)}
                    />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Thumbnail</p>
                    {thumbnailPreview
                      ? <img src={thumbnailPreview} alt="thumbnail" style={{ width: 100, height: 100, objectFit: 'contain' }} />
                      : <div style={{ width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>generating…</div>}
                  </div>
                </div>
              ) : thumbnailPreview ? (
                <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%', gap: 12 }}>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Thumbnail</p>
                    <img src={thumbnailPreview} alt="thumbnail" style={{ width: 100, height: 100, objectFit: 'contain' }} />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Preview</p>
                    <canvas ref={previewCanvasRef} style={{ width: 100, height: 100 }} />
                    <button type="button" onClick={() => previewPlayerRef.current?.start()} style={{ marginTop: 6, fontSize: 11, color: '#a78bfa', background: 'none', border: 'none', cursor: 'pointer' }}>
                      ▶ Replay
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <i className="fas fa-image" style={{ fontSize: 36, marginBottom: 10, opacity: 0.3 }} />
                  <p>Select an SVGA or VAP (.mp4) file to see preview</p>
                </>
              )}
            </div>
          </div>
          {statusText && <p className="status-blue">{statusText}</p>}
          {errorText && <div className="status-error">{errorText}</div>}
          <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
            <button type="submit" className="btn-upload" disabled={loading || !thumbnailPreview}>
              {loading ? <><i className="fas fa-spinner fa-spin" /> Uploading...</> : <><i className="fas fa-upload" /> Upload Gift</>}
            </button>
          </div>
        </form>
      </div>

      {/* Gift List */}
      <h4 className="section-heading">Uploaded Gifts</h4>
      {listLoading ? (
        <div className="dark-loading"><div className="spinner" /><p>Loading gifts...</p></div>
      ) : uploadedGifts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'rgba(255,255,255,0.3)' }}>
          <i className="fas fa-gift" style={{ fontSize: 36, marginBottom: 10, display: 'block' }} />
          No gifts uploaded yet.
        </div>
      ) : (
        <div className="dark-grid">
          {uploadedGifts.map(gift => (
            <div key={gift.id} className="dark-grid-item" style={{ position: 'relative' }}>
              <img src={gift.thumbnailUrl} alt={gift.name} />
              <span style={{ position: 'absolute', top: 6, left: 6, background: (gift.animType === 'vap' || gift.vapUrl) ? '#7c3aed' : '#2563eb', color: '#fff', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4 }}>
                {(gift.animType === 'vap' || gift.vapUrl) ? 'VAP' : 'SVGA'}
              </span>
              <div className="item-name" title={gift.name}>{gift.name}</div>
              <div className="item-price">{formatNumberPrecise(gift.price)} 💎</div>
              <button className="delete-btn" onClick={() => handleDelete(gift)} title="Delete">
                <i className="fas fa-times" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GiftUpload;
